/**
 * Beauty Autopilot: флаг виджета и купленный план лежат на GCB2-коннекторе проекта (GCB2 есть всегда).
 * Пишет их биллинг при покупке плана, ручная выдача из админки ставит только флаг — без плана.
 */
export type BeautyAutopilotPlan = 'basis' | 'optima'

/** Строки каталога биллинга, которые продаются с карточки Beauty Autopilot. */
export const BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS: Record<BeautyAutopilotPlan, string> = {
    basis: 'beautyAutopilotBasis',
    optima: 'beautyAutopilotOptima',
}

export const BEAUTY_AUTOPILOT_PLAN_LABELS: Record<BeautyAutopilotPlan, string> = {
    basis: 'Базис',
    optima: 'Оптима',
}

/** Подсказка у заблокированных на «Базисе» элементов: библиотека задач, автозаполнение. */
export const BEAUTY_AUTOPILOT_OPTIMA_HINT = 'Доступно в плане «Оптима»'

export function isBeautyAutopilotPlanIntegrationId(integrationId?: string | number | null): boolean {
    return Object.values(BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS).includes(String(integrationId ?? ''))
}

/**
 * Строки блока «Дополнительные подписки». Без тарифа в нём только план BA: он работает сам по себе.
 * Каналы и модули проекта без тарифа продлевать незачем, заблокированный проект их не видит.
 */
export function selectAddonRowsToShow<T extends { row?: { integrationId?: string | null } | null }>(
    rows: T[],
    hasTariff: boolean,
): T[] {
    return hasTariff ? rows : rows.filter((item) => isBeautyAutopilotPlanIntegrationId(item.row?.integrationId))
}

interface MarketplaceIntegrationStatusLike {
    id: string
    status?: string
    activeUntil?: string
}

/**
 * Карточка BA на витрине — не товар биллинга: продаются строки планов. Статус и срок карточки берём у
 * установленного плана, чтобы витрина, страница интеграции и кнопка показывали «Подключено».
 */
export function applyBeautyAutopilotPlanStatus<T extends MarketplaceIntegrationStatusLike>(
    integrations: T[],
    cardIntegrationId: string,
): T[] {
    const installedPlan = integrations.find(
        (integration) => isBeautyAutopilotPlanIntegrationId(integration.id) && integration.status === 'installed',
    )
    if (!installedPlan) return integrations

    return integrations.map((integration) =>
        String(integration.id) === String(cardIntegrationId)
            ? { ...integration, status: 'installed', activeUntil: installedPlan.activeUntil }
            : integration,
    )
}

interface BeautyAutopilotConnectorLike {
    connectorType?: string
    showBeautyAutopilot?: unknown
    beautyAutopilotPlan?: unknown
}

export interface BeautyAutopilotProjectLike {
    connectors?: BeautyAutopilotConnectorLike[] | null
}

function getGcb2Connector(project?: BeautyAutopilotProjectLike | null) {
    return project?.connectors?.find((connector) => connector?.connectorType === 'GCB2')
}

export function isBeautyAutopilotEnabled(project?: BeautyAutopilotProjectLike | null): boolean {
    return getGcb2Connector(project)?.showBeautyAutopilot === true
}

export function getBeautyAutopilotPlan(project?: BeautyAutopilotProjectLike | null): BeautyAutopilotPlan | null {
    const plan = getGcb2Connector(project)?.beautyAutopilotPlan
    return plan === 'basis' || plan === 'optima' ? plan : null
}

/**
 * Страницы проекта, открытые в статусе «только BA»: сам проект, Beauty Autopilot, биллинг и оплата,
 * маркетплейс. Остальные маршруты проекта ведут на страницу проекта.
 */
const BEAUTY_AUTOPILOT_ONLY_ALLOWED_PATHS: RegExp[] = [
    /^\/project\/[^/]+\/?$/,
    /^\/project\/[^/]+\/GCB2\/BeautyAutopilot(\/|$)/,
    /^\/project\/[^/]+\/billing(\/|$)/,
    /^\/project\/[^/]+\/payment\//,
    /^\/project\/[^/]+\/finances(\/|$)/,
    /^\/project\/[^/]+\/marketplace(\/|$)/,
]

export function isBeautyAutopilotOnlyAllowedPath(path: string): boolean {
    return BEAUTY_AUTOPILOT_ONLY_ALLOWED_PATHS.some((pattern) => pattern.test(path))
}

/**
 * Функции «Оптимы» (библиотека задач, автозаполнение). Виджет без плана — ручная выдача из админки:
 * у неё полный доступ, как до продажи планов. Та же логика на backend (requireBeautyAutopilotPlan).
 */
export function hasBeautyAutopilotOptimaFeatures(project?: BeautyAutopilotProjectLike | null): boolean {
    return isBeautyAutopilotEnabled(project) && getBeautyAutopilotPlan(project) !== 'basis'
}
